import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_LABELS, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Users, Plus, ShieldOff } from "lucide-react";

const ROLES = ["owner", "admin", "inventory_manager", "sales", "content_manager", "va", "view_only"];

export default function AdminUsers() {
  const { user } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "sales" });
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const r = await api.get("/admin/users"); setUsers(r.data); }
    catch (e) { if (e.response?.status === 403) setForbidden(true); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name || !draft.email || !draft.password) { toast.error("All fields required"); return; }
    try { await api.post("/admin/users", draft); toast.success("User created"); setOpen(false); setDraft({ name: "", email: "", password: "", role: "sales" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try { const body = { name: edit.name, role: edit.role, is_active: edit.is_active }; if (edit.password) body.password = edit.password; await api.put(`/admin/users/${edit.id}`, body); toast.success("User updated"); setEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deactivate = async (u) => { try { await api.delete(`/admin/users/${u.id}`); toast.success("User deactivated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (forbidden) return <Card className="p-8 text-center text-[var(--text-2)] border-[var(--border-lch)]"><ShieldOff className="mx-auto mb-2 text-[var(--muted)]" /> Only the Owner can manage team accounts.</Card>;

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Users className="text-[#c9a86a]" size={22} /><h1 className="text-2xl font-semibold">Team & Roles</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-btn" className="bg-[var(--panel-2)] text-[#c9a86a]"><Plus size={16} className="mr-2" />Add Team Member</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="user-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="user-email" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Temporary Password</Label><Input value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} data-testid="user-password" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Role</Label>
                <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="user-save" className="bg-[var(--panel-2)] text-[#c9a86a]">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[var(--border-lch)] overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase"><tr>
          <th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th>
          <th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-[var(--border-lch)]">
              <td className="p-3 font-medium">{u.name}</td><td className="p-3 text-[var(--text-2)]">{u.email}</td>
              <td className="p-3"><Badge variant="outline" className="bg-[var(--panel-2)] text-[#c9a86a] border-[#3a352b]">{ROLE_LABELS[u.role]}</Badge></td>
              <td className="p-3">{u.is_active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Active</Badge> : <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)]">Disabled</Badge>}</td>
              <td className="p-3 text-right space-x-2">
                <Button size="sm" variant="outline" onClick={() => setEdit({ ...u, password: "" })}>Edit</Button>
                {u.id !== user.id && u.is_active && <Button size="sm" variant="outline" className="text-red-600" onClick={() => deactivate(u)}>Disable</Button>}
              </td>
            </tr>
          ))}
        </tbody></table></div></Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          {edit && (<>
            <DialogHeader><DialogTitle>Edit {edit.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Role</Label>
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Reset Password (optional)</Label><Input value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} placeholder="Leave blank to keep" /></div>
              <div className="flex items-center gap-3"><Switch checked={edit.is_active} onCheckedChange={(v) => setEdit({ ...edit, is_active: v })} /><Label className="text-sm">Active</Label></div>
            </div>
            <DialogFooter><Button onClick={saveEdit} className="bg-[var(--panel-2)] text-[#c9a86a]">Save</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
